<script setup>
import {useI18n} from 'vue-i18n'
import {ref, watch} from 'vue'

const props = defineProps({
  shape: {type: String, default: 'RR'},
  showLabels: {type: Boolean, default: false},
  strokeColor: {type: String, default: '#D1D5DC'},
  fillColor: {type: String, default: '#D1D5DC'},
})

const {t: $t} = useI18n()

const canvas = ref(null)
const canvasSize = {width: 240, height: 240}
const ppi = 300
const PAD = 30

// Fallback labels — swap for $t(`shapes.${props.shape}`) once the keys exist.
const LABELS = {
  RR: 'round tube',
  RS: 'round rod',
  B: 'bend',
  F: 'flat',
  FH: 'flat-halfround',
  FO: 'flat-oval',
  N: 'slotted tube',
  PH: 'H-profile',
  PI: 'I-profile',
  PT: 'T-profile',
  PU: 'U-profile',
  PZ: 'Z-profile',
  S: 'hexagonal',
  V: 'rectangular/square',
  W: 'elbow',
  WU: 'elbow uneven',
}

watch(canvas, (el) => {
  if (!el) return
  setupCanvas()
  drawCanvas()
})

watch(
  () => [props.shape, props.showLabels, props.strokeColor, props.fillColor],
  () => drawCanvas(),
)

function setupCanvas() {
  const el = canvas.value
  const scale = ppi / 96
  el.width = canvasSize.width * scale
  el.height = canvasSize.height * scale
  el.getContext('2d').scale(scale, scale)
}

function getCtx() {
  return canvas.value.getContext('2d')
}

function clearCanvas(ctx) {
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height)
  ctx.restore()
}

function fitScale(bboxW, bboxH) {
  const availW = canvasSize.width - PAD * 2
  const availH = canvasSize.height - PAD * 2
  return Math.min(availW / bboxW, availH / bboxH)
}

function center() {
  return {x: canvasSize.width / 2, y: canvasSize.height / 2}
}

function applyBaseStyle(ctx) {
  ctx.strokeStyle = props.strokeColor
  ctx.fillStyle = props.fillColor
  ctx.lineWidth = 1.5
  ctx.lineJoin = 'round'
}

// --- drawing helpers ----------------------------------------------------

function poly(ctx, points) {
  ctx.beginPath()
  points.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)))
  ctx.closePath()
}

function paint(ctx, rule = 'nonzero') {
  ctx.fill(rule)
  ctx.stroke()
}

// --- individual shapes (cross-section silhouettes) ----------------------

const TAU = Math.PI * 2

function drawRoundRod(ctx) {
  // RS – solid bar
  const {x, y} = center()
  ctx.beginPath()
  ctx.arc(x, y, 70, 0, TAU)
  paint(ctx)
}

function drawRoundTube(ctx) {
  // RR – hollow ring
  const {x, y} = center()
  ctx.beginPath()
  ctx.arc(x, y, 72, 0, TAU)
  ctx.moveTo(x + 47, y) // separate subpath -> clean hole
  ctx.arc(x, y, 47, 0, TAU)
  paint(ctx, 'evenodd')
}

function drawSlottedTube(ctx) {
  const {x, y} = center()

  const ro = 72 // Outer radius
  const ri = 34 // Inner radius (bottom depth of the U)
  const hw = 15 // Half-width of the slot/channel

  const yo = Math.sqrt(ro * ro - hw * hw)

  const oL = Math.atan2(-yo, -hw)
  const oR = Math.atan2(-yo, hw)

  ctx.beginPath()

  // 1. The Outer Rim (Forward and Backward)
  ctx.arc(x, y, ro, oR, oL, false)
  ctx.arc(x, y, ro, oL, oR, true)

  // 2. The Left Channel Wall (Down and Up)
  ctx.moveTo(x - hw, y - yo)
  ctx.lineTo(x - hw, y + ri)
  ctx.lineTo(x - hw, y - yo)

  // 3. The Flat Bottom Channel Wall (Left-to-Right and Right-to-Left)
  ctx.moveTo(x - hw, y + ri)
  ctx.lineTo(x + hw, y + ri)
  ctx.lineTo(x - hw, y + ri)

  // 4. The Right Channel Wall (Down and Up)
  ctx.moveTo(x + hw, y - yo)
  ctx.lineTo(x + hw, y + ri)
  ctx.lineTo(x + hw, y - yo)

  // Set the stroke thickness here (e.g., 6 pixels)
  // Adjust this number up or down to get the exact thickness you want!
  ctx.lineWidth = 18

  paint(ctx)
}
function drawHex(ctx) {
  // S – hexagonal (flat-top)
  const {x, y} = center()
  const r = 72
  const pts = []
  for (let k = 0; k < 6; k++) {
    const a = (Math.PI / 180) * (60 * k)
    pts.push([x + r * Math.cos(a), y + r * Math.sin(a)])
  }
  poly(ctx, pts)
  paint(ctx)
}

function drawSquare(ctx) {
  // V – rectangular / square
  poly(ctx, [
    [55, 55],
    [185, 55],
    [185, 185],
    [55, 185],
  ])
  paint(ctx)
}

function drawFlat(ctx) {
  // F – flat bar
  poly(ctx, [
    [35, 93],
    [205, 93],
    [205, 147],
    [35, 147],
  ])
  paint(ctx)
}

function drawFlatHalfRound(ctx) {
  // FH – flat, one round side
  const {x} = center()
  const R = 72,
    baseY = 156
  ctx.beginPath()
  ctx.moveTo(x - R, baseY)
  ctx.arc(x, baseY, R, Math.PI, TAU) // top semicircle
  ctx.closePath()
  paint(ctx)
}

function drawFlatOval(ctx) {
  // FO – stadium / oval bar
  const {y} = center()
  const w = 170,
    r = 39
  const l = 120 - w / 2 + r,
    rr = 120 + w / 2 - r
  ctx.beginPath()
  ctx.moveTo(l, y - r)
  ctx.lineTo(rr, y - r)
  ctx.arc(rr, y, r, -Math.PI / 2, Math.PI / 2, false)
  ctx.lineTo(l, y + r)
  ctx.arc(l, y, r, Math.PI / 2, (3 * Math.PI) / 2, false)
  ctx.closePath()
  paint(ctx)
}

function drawBend(ctx) {
  // B – 90° pipe elbow (L-shape with rounded corner)
  const hw = 13 // half the pipe band width
  const cx = 86,
    cy = 154 // inner corner where the two legs meet
  const R = 56 // centerline bend radius
  const yTop = 46 // top end of the vertical leg
  const xRight = 190 // right end of the horizontal leg
  const Cx = cx + R,
    Cy = cy - R // bend arc center
  const ro = R + hw,
    ri = R - hw // outer / inner edge radii

  ctx.beginPath()
  ctx.moveTo(cx - hw, yTop) // outer-left, top
  ctx.lineTo(cx - hw, cy - R) // down outer edge of vertical leg
  ctx.arc(Cx, Cy, ro, Math.PI, Math.PI / 2, true) // outer bend
  ctx.lineTo(xRight, cy + hw) // along outer edge of horizontal leg
  ctx.lineTo(xRight, cy - hw) // right end cap
  ctx.lineTo(Cx, cy - hw) // back along inner edge of horizontal leg
  ctx.arc(Cx, Cy, ri, Math.PI / 2, Math.PI, false) // inner bend
  ctx.lineTo(cx + hw, yTop) // up inner edge of vertical leg
  ctx.closePath() // top cap
  paint(ctx)
}

function drawH(ctx) {
  // PH – H-profile (two vertical flanges, horizontal crossbar)
  poly(ctx, [
    [50, 50],
    [50, 190],
    [76, 190],
    [76, 133],
    [164, 133],
    [164, 190],
    [190, 190],
    [190, 50],
    [164, 50],
    [164, 107],
    [76, 107],
    [76, 50],
  ])
  paint(ctx)
}

function drawI(ctx) {
  // PI – I-profile (tall, slim)
  poly(ctx, [
    [68, 45],
    [172, 45],
    [172, 67],
    [130, 67],
    [130, 173],
    [172, 173],
    [172, 195],
    [68, 195],
    [68, 173],
    [110, 173],
    [110, 67],
    [68, 67],
  ])
  paint(ctx)
}

function drawT(ctx) {
  // PT – T-profile
  poly(ctx, [
    [45, 50],
    [195, 50],
    [195, 76],
    [134, 76],
    [134, 190],
    [106, 190],
    [106, 76],
    [45, 76],
  ])
  paint(ctx)
}

function drawU(ctx) {
  // PU – U-profile / channel (opening up)
  poly(ctx, [
    [50, 55],
    [50, 185],
    [190, 185],
    [190, 55],
    [164, 55],
    [164, 159],
    [76, 159],
    [76, 55],
  ])
  paint(ctx)
}

function drawZ(ctx) {
  // PZ – Z-profile
  poly(ctx, [
    [108, 50],
    [185, 50],
    [185, 74],
    [132, 74],
    [132, 190],
    [55, 190],
    [55, 166],
    [108, 166],
  ])
  paint(ctx)
}

function drawAngle(ctx) {
  // W – equal-leg angle (L)
  poly(ctx, [
    [50, 50],
    [78, 50],
    [78, 162],
    [190, 162],
    [190, 190],
    [50, 190],
  ])
  paint(ctx)
}

function drawAngleUneven(ctx) {
  // WU – unequal-leg angle (L)
  poly(ctx, [
    [65, 45],
    [91, 45],
    [91, 169],
    [175, 169],
    [175, 195],
    [65, 195],
  ])
  paint(ctx)
}

const DRAWERS = {
  RR: drawRoundTube,
  RS: drawRoundRod,
  B: drawBend,
  F: drawFlat,
  FH: drawFlatHalfRound,
  FO: drawFlatOval,
  N: drawSlottedTube,
  PH: drawH,
  PI: drawI,
  PT: drawT,
  PU: drawU,
  PZ: drawZ,
  S: drawHex,
  V: drawSquare,
  W: drawAngle,
  WU: drawAngleUneven,
}

function drawLabel(ctx) {
  const text = LABELS[props.shape] ?? props.shape
  ctx.save()
  ctx.fillStyle = props.strokeColor
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = '600 15px system-ui, -apple-system, sans-serif'
  ctx.fillText(text, 120, 222)
  ctx.restore()
}

function drawCanvas() {
  const ctx = getCtx()
  clearCanvas(ctx)
  applyBaseStyle(ctx)
  const draw = DRAWERS[props.shape] ?? drawRoundRod
  draw(ctx)
  if (props.showLabels) drawLabel(ctx)
}
</script>

<template>
  <canvas ref="canvas" style="width: 100%; height: 100%; display: block" />
</template>

<style scoped lang="scss"></style>
